import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery } from "../_app/lib/query";
import { AjaxResponse } from "../_app/api";
import { useFeedbackAlerts } from "../_app/hooks";
import { genericError } from "../_app/utils/text";
import { createTicket, createTicketComment, getTicketById, getTicketComments, getTickets } from "./api";
import { CreateTicket, GetTicketCommentsResponse } from "./types";
import { useAuthSession } from "../auth/hooks";
import { getFullName } from "../user/utils";
import { useContextProvider } from "../context/providers/ContextProvider";

export const TICKETS_QUERY_KEY = "tickets";
export const TICKET_QUERY_KEY = "ticket";
export const TICKET_COMMENTS_QUERY_KEY = "ticketComments";

export function useTickets(page: number, limit: number, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: [TICKETS_QUERY_KEY, { accountId, page, limit }],
    queryFn: () => getTickets(accountId, page, limit),

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },

    ...options,
  });
}

export function useSingleTicket(ticketId: string, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: [TICKET_QUERY_KEY, { accountId, ticketId }],
    queryFn: () => getTicketById(accountId, ticketId),

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },

    ...options,
  });
}

export function useTicketComments(ticketId: string, limit: number = 25, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<GetTicketCommentsResponse, AjaxResponse>({
    queryKey: [TICKET_COMMENTS_QUERY_KEY, { ticketId }],
    queryFn: ({ pageParam }) => getTicketComments({ accountId, ticketId, page: pageParam as number, pageSize: limit }),
    initialPageParam: 0,
    getNextPageParam: ({ page, pageSize, total }: GetTicketCommentsResponse) => {
      const end = typeof total === "number" ? total : 100;
      if ((page + 1) * pageSize < end) {
        return page + 1;
      } else {
        return undefined;
      }
    },
    ...options,
  });
}

export function useCreateTicket(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (ticket: CreateTicket) => createTicket(accountId, ticket),
    ...options,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TICKETS_QUERY_KEY],
      });
      options?.onSuccess?.();
      setFeedbackAlertSuccess("Ticket created");
    },

    onError: () => {
      setFeedbackAlertError(genericError());
    },
  });
}

export function useCreateTicketComment(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { data: session } = useAuthSession();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: ({ ticketId, comment }: any) => createTicketComment(accountId, ticketId, comment),

    onMutate: async ({ ticketId, comment }) => {
      const queryKey = [TICKET_COMMENTS_QUERY_KEY, { accountId, ticketId }];
      await queryClient.cancelQueries({
        queryKey: queryKey,
      });
      const rollbackState = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) => {
        const temp = {
          id: String(Date.now()),
          created: null,
          createdBy: getFullName(session?.userProfile),
          internal: true,
          comment,
        };
        if (!old) return { pages: [{ page: 0, pageSize: 25, list: [temp] }] };
        const oldPage1 = old.pages[0];
        const newPage1 = {
          ...oldPage1,
          list: [temp, ...oldPage1.list],
        };
        return {
          ...old,
          pages: [newPage1, ...old.pages.slice(1)],
        };
      });
      return { rollbackState };
    },

    onError: (error: any, { ticketId }, context: { rollbackState: unknown } | undefined) => {
      const queryKey = [TICKET_COMMENTS_QUERY_KEY, { ticketId }];
      if (context?.rollbackState) queryClient.setQueryData(queryKey, context?.rollbackState);
      setFeedbackAlertError(error?.data?.message || genericError());
    },

    onSuccess: (data, { ticketId }) => {
      const queryKey = [TICKET_COMMENTS_QUERY_KEY, { ticketId }];
      if (ticketId) {
        queryClient.invalidateQueries({
          queryKey: queryKey,
        });
      }
      setFeedbackAlertSuccess("Comment added");
    },

    ...options,
  });
}
